/**
 * NFL week-based stat range — the football sibling of `@/lib/timeFilter`.
 *
 * ── WHY THIS IS NOT timeFilter.ts ───────────────────────────────────────────
 *
 * MLB windows on DATES: `getDateRange` returns `{ start, end }` bare date strings and the
 * query filters `game_date`. That cannot work here. `nfl_player_game_stats` has no date to
 * window on — it is keyed on `week` + `season_type`, and football is played in weeks, not
 * rolling day windows. A 7-day window over an NFL schedule would slice a single week
 * differently depending on which day you asked.
 *
 * So this resolves a range to WEEK NUMBERS, and every caller pairs it with
 * `NFL_STAT_SEASON_TYPE` below.
 *
 * ── THE EMPTY RANGE IS A REAL ANSWER ────────────────────────────────────────
 *
 * `weeks: []` is not a failure — it is "this range contains no weeks", which happens
 * legitimately in week 1 (there is no last week), on a bye, and whenever a league's
 * `current_week` has run ahead of the ingested stats. Callers must render an honest empty
 * state for it. They must NOT render zeros: a roster of 0.0s is indistinguishable from a
 * roster that scored nothing, which is SAB-809's lesson in a different costume.
 *
 * `weeks: null` means "every week" (Season) and is the only value that means no filter.
 */

/** My Team defaults to 'week' (This Week). 'week' and 'last' are week-indexed; 'season' is not. */
export type NflRange = 'season' | 'week' | 'last';

export const NFL_RANGE_OPTIONS: { value: NflRange; label: string }[] = [
  { value: 'season', label: 'Season' },
  { value: 'week', label: 'This Week' },
  { value: 'last', label: 'Last Week' },
];

/**
 * SAB-746: every NFL stat read MUST pin the phase. `nfl_player_game_stats` carries PRE and
 * REG rows that share week numbers, so `week = 1` alone mixes a preseason game into the
 * regular season. 539 prod player-weeks were already double-counted this way once.
 */
export const NFL_STAT_SEASON_TYPE = 'REG' as const;

export interface NflWeekWindow {
  /** Weeks to filter on. `null` = no week filter (Season). `[]` = the range is empty. */
  weeks: number[] | null;
  /** True when the range resolves to no weeks — render an empty state, never zeros. */
  isEmpty: boolean;
  /** Why it is empty, for the empty-state copy. `null` when it is not empty. */
  emptyReason: 'no-current-week' | 'no-prior-week' | null;
}

/**
 * Resolve a range to the weeks it covers.
 *
 * `currentWeek` comes from the league (`nfl_leagues.current_week`), not from a clock — a
 * league can legitimately sit on a week whose games have not been ingested yet, and the
 * caller must be able to say so rather than silently showing the previous week's numbers.
 */
export function resolveWeekRange(range: NflRange, currentWeek: number | null | undefined): NflWeekWindow {
  if (range === 'season') {
    return { weeks: null, isEmpty: false, emptyReason: null };
  }

  const wk = typeof currentWeek === 'number' && Number.isFinite(currentWeek) ? Math.floor(currentWeek) : null;

  if (range === 'week') {
    if (wk === null || wk < 1) return { weeks: [], isEmpty: true, emptyReason: 'no-current-week' };
    return { weeks: [wk], isEmpty: false, emptyReason: null };
  }

  // 'last' — week 1 has no predecessor, and that is a normal state for a third of September,
  // not an error. Same for a league whose current_week is missing.
  if (wk === null || wk - 1 < 1) return { weeks: [], isEmpty: true, emptyReason: 'no-prior-week' };
  return { weeks: [wk - 1], isEmpty: false, emptyReason: null };
}

/** Label for the score eyebrow — "Season", "Week 3", "Week 2". */
export function rangeLabel(range: NflRange, currentWeek: number | null | undefined): string {
  const w = resolveWeekRange(range, currentWeek);
  if (range === 'season') return 'Season';
  if (w.isEmpty || !w.weeks?.length) return range === 'week' ? 'This Week' : 'Last Week';
  return `Week ${w.weeks[0]}`;
}
